import { ArrowRight, Search, SlidersHorizontal, UserRoundSearch } from 'lucide-react';
import { FormEvent, useEffect, useState } from 'react';
import { Loading } from '../components/Loading';
import { Pagination } from '../components/Pagination';
import { StatusBadge } from '../components/StatusBadge';
import { api, dateTime } from '../lib/api';
import { Link, useNavigate, useSearchParams } from '../lib/router';
import type { Customer } from '../lib/types';

const PAGE_SIZE = 20;

export function CustomersPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('search') ?? '');
  const [items, setItems] = useState<Customer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const isReviewQueue = params.get('queue') === 'kyc';
  const status = params.get('status') ?? (isReviewQueue ? 'REVIEW_QUEUE' : '');
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const page = Math.max(1, Number(params.get('page')) || 1);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api<{ items: Customer[]; total: number }>(
      `/customers?${new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
        ...(params.get('search') ? { search: params.get('search')! } : {}),
        ...(status ? { status } : {}),
      })}`,
    )
      .then((result) => {
        if (!active) return;
        setItems(result.items);
        setTotal(result.total);
      })
      .catch(() => { if (active) setError('Não foi possível carregar os clientes. Tenta novamente.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [params, status, page, retry]);

  useEffect(() => setSearch(params.get('search') ?? ''), [params]);

  function submit(event: FormEvent) {
    event.preventDefault();
    const next = new URLSearchParams(params);
    if (search.trim()) next.set('search', search.trim());
    else next.delete('search');
    next.delete('page');
    setParams(next);
  }

  function changeStatus(nextStatus: string) {
    const next = new URLSearchParams(params);
    if (nextStatus) next.set('status', nextStatus);
    else next.delete('status');
    next.delete('page');
    setParams(next);
  }

  function changePage(nextPage: number) {
    const next = new URLSearchParams(params);
    if (nextPage > 1) next.set('page', String(nextPage));
    else next.delete('page');
    setParams(next);
  }

  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Gestão de clientes</span>
          <h1>{isReviewQueue ? 'Revisão KYC' : 'Clientes'}</h1>
          <p>{isReviewQueue ? 'Clientes pendentes, em análise ou a aguardar mais informação.' : `${total} registos encontrados nos critérios atuais.`}</p>
        </div>
      </div>

      <section className="panel">
        <div className="filters">
          <form className="search-field" onSubmit={submit}>
            <Search size={18} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Pesquisar por nome, email, telefone ou ID"
              aria-label="Pesquisar clientes"
            />
          </form>
          <div className="select-field">
            <SlidersHorizontal size={17} />
            <select
              value={status}
              onChange={(event) => changeStatus(event.target.value)}
              aria-label="Filtrar por estado KYC"
            >
              {isReviewQueue ? <option value="REVIEW_QUEUE">Todos os processos por concluir</option> : <option value="">Todos os estados</option>}
              <option value="PENDING">Pendente</option>
              <option value="IN_REVIEW">Em análise</option>
              {!isReviewQueue && <option value="APPROVED">Aprovado</option>}
              {!isReviewQueue && <option value="REJECTED">Rejeitado</option>}
              <option value="MORE_INFORMATION_REQUIRED">Mais informação</option>
            </select>
          </div>
        </div>

        {loading ? (
          <Loading label="A procurar clientes" />
        ) : error ? (
          <div className="empty-state" role="alert"><h3>Erro ao carregar clientes</h3><p>{error}</p><button className="button button--secondary" onClick={() => setRetry(value => value + 1)}>Tentar novamente</button></div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <UserRoundSearch size={34} />
            <h3>{isReviewQueue ? 'Nenhum processo KYC nestes critérios' : 'Nenhum cliente encontrado'}</h3>
            <p>{isReviewQueue ? 'Os clientes já aprovados ou rejeitados estão na lista de clientes. Podes também ajustar a pesquisa e o estado.' : 'Altere a pesquisa ou os filtros selecionados.'}</p>
            {isReviewQueue && <Link className="button button--secondary" to="/customers">Ver todos os clientes</Link>}
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Telefone</th>
                  <th>Registo</th>
                  <th>KYC</th>
                  <th>Risco</th>
                  <th>Wallet</th>
                  <th>Eupago</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {items.map((customer) => (
                  <tr
                    key={customer.id}
                    className="table-row-link"
                    onClick={() => navigate(`/customers/${customer.id}`)}
                  >
                    <td>
                      <div className="customer-cell">
                        <span className="avatar avatar--small">
                          {customer.fullName.slice(0, 2).toUpperCase()}
                        </span>
                        <div>
                          <strong>{customer.fullName}</strong>
                          <small>{customer.email ?? customer.phone}</small>
                        </div>
                      </div>
                    </td>
                    <td>{customer.phone}</td>
                    <td>{dateTime(customer.createdAt)}</td>
                    <td><StatusBadge value={customer.kycStatus} /></td>
                    <td><StatusBadge value={customer.riskLevel} /></td>
                    <td><StatusBadge value={customer.walletStatus} /></td>
                    <td><StatusBadge value={customer.partnerStatus} /></td>
                    <td>
                      <Link className="table-action" to={`/customers/${customer.id}`}>
                        Abrir <ArrowRight size={15} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && !error && <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={changePage}
        />}
      </section>
    </div>
  );
}
